import { DownloadCloud02, Eye, ZoomIn, ZoomOut } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import TabButton from 'src/components/TabButton';
import TooltipCmp from 'src/components/TooltipCmp';
import { trackDocumentDownload } from 'src/services/DocumentsService';
import { getDateWithSuffixFormat, getFormattedDate } from 'src/utils/CommonFunctions';

// Use CDN worker with .mjs extension matching the installed pdfjs-dist v5
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOM_LEVELS = [50, 75, 100, 125, 150];
const PDF_BASE_WIDTH = 850;

interface PodDetailModalProps {
    /** The Invoice tab row — carries podDocuments plus the invoice id used to fetch order context. */
    invoiceData: any;
    isOpen: boolean;
    handleClose: () => void;
}

/**
 * POD modal opened from the Document Hub table's "View POD" link. Mirrors
 * InvoiceSummaryModal's layout with ONE preview tab per POD file (orders can carry several
 * PODs) — nothing else. The former "POD Details" tab (order id, delivery date, addresses)
 * was removed at the client's request (2026-09-15): the modal is preview-only.
 */
const PodDetailModal: React.FC<PodDetailModalProps> = ({ invoiceData, isOpen, handleClose }) => {
    const { t } = useTranslation();

    const [currentStep, setCurrentStep] = useState(1);

    // PDF viewer state
    const [numPages, setNumPages] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(50);
    const [pdfNaturalHeight, setPdfNaturalHeight] = useState(0);

    const pdfInnerRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const lastZoomAtRef = useRef(0);
    const zoomAnchorRef = useRef<{ oldZoom: number; cursorX: number; cursorY: number; contentX: number; contentY: number } | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
        }
    }, [isOpen]);

    // Each preview tab shows a different file — reset the viewer whenever the tab changes
    // (also covers close/reopen, since closing resets currentStep to 1).
    useEffect(() => {
        setNumPages(0);
        setZoomLevel(50);
        setPdfNaturalHeight(0);
    }, [currentStep]);

    // Track natural height of rendered PDF for zoom scaling
    useEffect(() => {
        const node = pdfInnerRef.current;
        if (!node) return;
        const ro = new ResizeObserver((entries) => {
            const h = entries[0]?.contentRect?.height ?? 0;
            if (h > 0) setPdfNaturalHeight(h);
        });
        ro.observe(node);

        return () => ro.disconnect();
    }, [numPages, invoiceData]);

    const stepZoom = useCallback((direction: 1 | -1, cursor?: { clientX: number; clientY: number }) => {
        setZoomLevel((current) => {
            const idx = ZOOM_LEVELS.indexOf(current);
            const nextIdx = Math.min(ZOOM_LEVELS.length - 1, Math.max(0, (idx === -1 ? 2 : idx) + direction));
            const next = ZOOM_LEVELS[nextIdx];
            const scrollEl = scrollContainerRef.current;

            if (cursor && scrollEl && next !== current) {
                const rect = scrollEl.getBoundingClientRect();
                zoomAnchorRef.current = {
                    oldZoom: current,
                    cursorX: cursor.clientX - rect.left,
                    cursorY: cursor.clientY - rect.top,
                    contentX: scrollEl.scrollLeft + cursor.clientX - rect.left,
                    contentY: scrollEl.scrollTop + cursor.clientY - rect.top,
                };
            }

            return next;
        });
    }, []);

    // Ctrl+wheel zoom — must be after stepZoom definition
    useEffect(() => {
        const node = previewContainerRef.current;
        if (!node) return;

        const onWheel = (e: WheelEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            const now = Date.now();
            if (now - lastZoomAtRef.current < 80) return;
            lastZoomAtRef.current = now;
            stepZoom(e.deltaY < 0 ? 1 : -1, { clientX: e.clientX, clientY: e.clientY });
        };
        node.addEventListener('wheel', onWheel, { passive: false });

        return () => node.removeEventListener('wheel', onWheel);
    }, [stepZoom]);

    useLayoutEffect(() => {
        const scrollEl = scrollContainerRef.current;
        const anchor = zoomAnchorRef.current;
        if (!scrollEl || !anchor) return;
        const ratio = zoomLevel / anchor.oldZoom;
        scrollEl.scrollLeft = anchor.contentX * ratio - anchor.cursorX;
        scrollEl.scrollTop = anchor.contentY * ratio - anchor.cursorY;
        zoomAnchorRef.current = null;
    }, [zoomLevel]);

    // Must be before early return — hooks cannot be called conditionally.
    // Sidebar tab values: 1..N = one preview tab per POD file (first file opens by default).
    // `file` is the sanitized storage key; `fileName` is only the display name (PodSummaryModal contract).
    const pods: any[] = invoiceData?.podDocuments ?? [];
    const pod = pods[currentStep - 1] ?? pods[0];
    const fileUrl: string | null = useMemo(() => (pod?.fileUrl && (pod?.file || pod?.fileName) ? `${pod.fileUrl}${pod.file || pod.fileName}` : null), [pod]);
    const extension = useMemo(() => pod?.fileName?.split('.')?.pop()?.toLowerCase(), [pod]);

    if (!isOpen) return null;

    const isPdf = extension === 'pdf';
    const isImage = ['png', 'jpg', 'jpeg'].includes(extension || '');

    const handleDownload = async () => {
        if (!fileUrl) return;
        const name = pod?.fileName || 'pod';

        // Portal usage: report the download (fire-and-forget). invoiceData is the invoice row, which carries walOrderId.
        trackDocumentDownload({ docType: 'pod', entityType: 'order', entityId: invoiceData?.walOrderId ?? invoiceData?.order?.walOrderId });

        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(fileUrl, '_blank');
        }
    };

    // One preview tab per POD (client request 2026-09-17): a single POD reads "Preview" like the
    // invoice modal; several PODs are labeled by leg id ONLY (BE-derived like WAL's Leg column,
    // "Master" for order-level docs) — no "Preview -" prefix, so the ids fit on one line.
    const tabArray = pods.map((p: any, i: number) => ({
        value: i + 1,
        name: pods.length > 1 ? p?.legLabel || t('Orders.Master') : t('DocumentHub.Preview'),
        icon: <Eye className="vertical-secondary-tab-item-icon flex-none" />,
    }));

    return (
        <CommonModal
            title={
                <div className="flex items-center gap-1.5">
                    <span>{invoiceData?.orderId ? `#${invoiceData.orderId}` : pod?.fileName || t('DocumentHub.Pod')}</span>
                    <BadgeCmp style="modern" type="gray">
                        {t('DocumentHub.POD')}
                    </BadgeCmp>
                </div>
            }
            handleClose={handleClose}
            size="max-w-[980px] h-[865px]"
            modalContentSidebarClass="!overflow-hidden !scrollbar-hide"
            modalClassName="overflow-hidden min-w-0 flex-1 h-full flex flex-col"
            ModalWithSidebar={true}
            ContentLeft={
                <TabButton
                    activeClassName="tab-box-vertical-active"
                    isVerticalBoxTab={true}
                    tabParentClassName="min-h-[600px]"
                    isModalTabVerticalSidebar={true}
                    tabArray={tabArray}
                    isActive={currentStep}
                    // File-name labels can be long — cap the width so the sidebar keeps its shape
                    tabTextClass="max-w-[150px]"
                    handleOnClick={(e: any) => setCurrentStep(Number(e.target.dataset.value))}
                />
            }
        >
            <div className="flex flex-row w-full flex-1 h-full overflow-hidden">
                {/* ── One preview tab per POD file ── */}
                {pod && (
                    <div className="flex flex-col w-full h-full overflow-hidden">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between px-4 pt-4 bg-white800 flex-none space-x-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm font-semibold text-textSecondary truncate block">{pod?.fileName || t('DocumentHub.Preview')}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-none">
                                {isPdf && fileUrl && (
                                    <>
                                        <TooltipCmp message={t('Common.Zoom_In')}>
                                            <ButtonCmp
                                                size="none"
                                                icon={<ZoomIn className="w-4 h-4 text-grayLight600" />}
                                                onClick={() => stepZoom(1)}
                                                disabled={zoomLevel >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                                            >
                                                <></>
                                            </ButtonCmp>
                                        </TooltipCmp>
                                        <TooltipCmp message={t('Common.Zoom_Out')}>
                                            <ButtonCmp size="none" icon={<ZoomOut className="w-4 h-4 text-grayLight600" />} onClick={() => stepZoom(-1)} disabled={zoomLevel <= ZOOM_LEVELS[0]}>
                                                <></>
                                            </ButtonCmp>
                                        </TooltipCmp>
                                        <div className="w-px h-4 bg-utilityGray200" />
                                    </>
                                )}
                                <TooltipCmp message={t('Common.Download')}>
                                    <ButtonCmp size="none" icon={<DownloadCloud02 className="w-4 h-4 text-grayLight600" />} onClick={handleDownload} disabled={!fileUrl}>
                                        <></>
                                    </ButtonCmp>
                                </TooltipCmp>
                            </div>
                        </div>

                        {/* Preview Canvas Area */}
                        <div ref={previewContainerRef} className="flex-1 overflow-auto custom-scrollbar-v2 p-5 bg-white800">
                            {isPdf && fileUrl ? (
                                <div ref={scrollContainerRef} className="w-full h-full overflow-auto custom-scrollbar-v2 rounded-xl bg-white800 p-8">
                                    <div
                                        className="mx-auto"
                                        style={{
                                            width: PDF_BASE_WIDTH * (zoomLevel / 100),
                                            height: pdfNaturalHeight ? pdfNaturalHeight * (zoomLevel / 100) : undefined,
                                        }}
                                    >
                                        <div
                                            ref={pdfInnerRef}
                                            style={{
                                                width: PDF_BASE_WIDTH,
                                                transform: `scale(${zoomLevel / 100})`,
                                                transformOrigin: 'top left',
                                            }}
                                        >
                                            <Document
                                                file={fileUrl}
                                                onLoadSuccess={({ numPages: total }) => setNumPages(total)}
                                                loading={<p className="text-sm text-grayLight600 text-center py-16">{t('Common.Loading')}</p>}
                                                error={<p className="text-sm text-grayLight600 text-center py-16">{t('DocumentHub.No_Preview_Available')}</p>}
                                            >
                                                {Array.from({ length: numPages }, (_, i) => (
                                                    <Page
                                                        key={`page_${i + 1}`}
                                                        pageNumber={i + 1}
                                                        width={PDF_BASE_WIDTH}
                                                        renderAnnotationLayer={false}
                                                        renderTextLayer={false}
                                                        className="mb-4 shadow-md bg-white"
                                                    />
                                                ))}
                                            </Document>
                                        </div>
                                    </div>
                                </div>
                            ) : isImage && fileUrl ? (
                                <div className="w-full h-full flex items-center justify-center overflow-auto custom-scrollbar-v2 rounded-xl bg-white800">
                                    <img src={fileUrl} alt={pod?.fileName} className="max-w-full max-h-full object-contain shadow-md" />
                                </div>
                            ) : (
                                <div className="h-full flex items-center justify-center text-sm text-grayLight600">{t('DocumentHub.No_Preview_Available')}</div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-4 pb-4 flex-none bg-white800">
                            {pod?.dateUploaded && (
                                <p
                                    className="text-xs leading-[18px] text-gray500 font-normal"
                                    dangerouslySetInnerHTML={{
                                        __html: getDateWithSuffixFormat(getFormattedDate(pod.dateUploaded, 'MMMM Do, YYYY', true, true)),
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </CommonModal>
    );
};

export default PodDetailModal;
